/**
 * @file AI报告入口
 * @author izzieyspu
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Frame, Text, Link } from '@tencent/odc';
import dayjs from 'dayjs';
import _ from 'lodash';

import { saveCombinedValue, getFromCombinedValue } from '@/utils/storage';
import GuideTip from '@/adq-components/guide-tip';
import globalStore from '@/stores';
import { FLAGS } from '@/constants/Constants';

import useAIAssistant from '../../hooks/use-ai-assistant';
import { USER_AI_CONFIG_STORAGE_KEY } from '../../constants';

// 存储所有用户的阅读状态
const STORAGE_KEY_USER_VIEWED_DATE = 'ai-report-entry-viewed-date';

// 存储功能上新提示已读
const STORAGE_KEY_CLOSE_TIP = 'ai-report-entry';

const AIReportEntry = (props: { reportActionName: string }) => {
  const { reportActionName } = props;
  const userId = _.get(globalStore, 'account.user_id');
  const { showAIAssistant, aiAssistantModal } = useAIAssistant();
  const [viewedDate, setViewedDate] = useState<string | null>(() => getFromCombinedValue(USER_AI_CONFIG_STORAGE_KEY, STORAGE_KEY_USER_VIEWED_DATE, { userId }));

  // 获取今天的日期字符串
  const today = useMemo(() => dayjs().format('YYYY-MM-DD'), []);

  const hasCloseTip = useMemo(() => !!getFromCombinedValue(FLAGS.GUIDE_TIP_STORAGE.GLOBAL_KEY, STORAGE_KEY_CLOSE_TIP, { defaultValue: 0 }), []);

  // 更新当前用户的阅读状态
  const updateUserViewState = useCallback(
    (newViewedDate: string | null) => {
      setViewedDate(newViewedDate);
      saveCombinedValue(USER_AI_CONFIG_STORAGE_KEY, STORAGE_KEY_USER_VIEWED_DATE, newViewedDate, { userId });
    },
    [userId],
  );

  // 关闭上新提示
  const handleCloseTip = useCallback(() => {
    saveCombinedValue(FLAGS.GUIDE_TIP_STORAGE.GLOBAL_KEY, STORAGE_KEY_CLOSE_TIP, 1);
  }, []);

  // 唤起AI助理
  const handleAIAssistantShow = useCallback(() => {
    handleCloseTip();
    showAIAssistant({
      showReport: true,
    });
    updateUserViewState(today);
  }, [updateUserViewState, handleCloseTip, today]);

  // 当日期变化时，如果存储的日期不是今天，则重置为未读
  useEffect(() => {
    if (viewedDate !== null && viewedDate !== today && userId) {
      updateUserViewState(null);
    }
  }, [today, viewedDate, userId, updateUserViewState]);

  // 今日日报是否已被查看
  const isViewed = viewedDate === today;
  return (
    <>
      <style>{`
        @keyframes expandWidth {
          from {
            max-width: 32px;
          }
          to {
            max-width: 240px;
          }
        }
        .tw-animate-expand-width {
          max-width: 32px;
          animation: expandWidth 0.5s ease-in-out forwards;
        }
      `}</style>
      {aiAssistantModal}
      <GuideTip
        markKey={STORAGE_KEY_CLOSE_TIP}
        title="AI运营日报全新上线！"
        content="一键生成昨日投放报告，快速洞悉营销单元表现，让决策更精准"
        submitLabel="立即查看"
        placement="bottom"
        showCount={1}
        closable={true}
        onConfirm={handleAIAssistantShow}
        onClose={handleCloseTip}
      >
        <Frame position='relative'>
          <Frame
            className={`tw-group tw-flex tw-items-center tw-gap-1 tw-px-2 tw-h-[26px] tw-rounded-full tw-cursor-pointer tw-relative tw-overflow-hidden ${!hasCloseTip ? 'tw-animate-expand-width' : ''}`}
            style={{
              background: '#EAF3FF',
              border: '0.5px solid #B3D5FF',
              width: 'auto',
            }}
            onClick={handleAIAssistantShow}
            data-report={reportActionName}
          >
            <img
              src="https://qzonestyle.gdtimg.com/gdt_ui_proj/imghub/dist/1767002276497.png?max_age=31536000"
              width={16}
              height={16}
            />
            <Text
              type="primary"
              size="small"
              label={
                <>你的专属报告已生成 <Link underline>点击查收</Link></>
              }
              className="tw-leading-[22px]"
              nowrap
            />
            <img
              src="https://qzonestyle.gdtimg.com/gdt_ui_proj/imghub/dist/1767927390445.png?max_age=31536000"
              width={22}
            />
          </Frame>
          {!isViewed && (
            <Frame
              className="tw-w-3 tw-h-3 tw-rounded-full tw-border-[2px] tw-border-white-00 tw-bg-[#FF7E00] tw-absolute tw-top-[-4px] tw-right-[-2px]"
            />
          )}
        </Frame>
      </GuideTip>
    </>
  );
};

export default AIReportEntry;
